import {_} from 'vue-underscore';
import api from '../utils/api.js';
import events from '../utils/events.js';
import { trackEvent } from '../utils/tracking';
import { OUTFIT_TYPES } from '../../config.js';
import { getOutfitNamespace } from '../utils/outfits.js';

export const outfits = {
    namespaced: true,
    state: {
        currentOutfit: {},
        detailedOutfits: {},
    },
    getters: {
        currentOutfit(state) {
            return state.currentOutfit;
        },
    },
    mutations: {
        toggleOutfitProduct(state, payload) {
            const { index } = payload;
            state.currentOutfit.products.splice(
                index,
                1,
                Object.assign({}, state.currentOutfit.products[index], {
                    selected: !state.currentOutfit.products[index].selected
                })
            );
        },
        setCurrentOutfit(state, payload){
            const { outfit, outfitType, productIdsIgnoreSelect = [] } = payload;
            let { outfitFromState } = payload;
            const { detailedOutfits, currentOutfit: { currentProductIndex } = {} } = state;
            outfitFromState = outfitFromState || detailedOutfits[outfit.outfit_id];

            const products = outfit.products.map((product) => {
                if(!outfitFromState || !outfitFromState.products) {
                    return {
                        product_option_value_id_size: null,
                        selected: true,
                        ...product,
                    };
                }

                const productsFromState = outfitFromState.products;
                const productFromState = productsFromState[product.product_id] 
                    || (Array.isArray(productsFromState) && productsFromState.find(productFromState => productFromState.product_id === product.product_id))
                    || {};
                const selectStatus = !productIdsIgnoreSelect.find(id => id === product.product_id);

                return {
                    product_option_value_id_size: null,
                    ...productFromState,
                    ...product,
                    isPristine: {
                        color: true,
                        size: true,
                    },
                    selected: selectStatus,
                };
            });

            state.currentOutfit = {
                currentProductIndex,
                ...outfit,
                type: outfitType || outfit.type, // Prioritize calculated outfit type over the API type
                products,
            }
            if(state.currentOutfit.isDetailed) state.detailedOutfits[state.currentOutfit.outfit_id] = state.currentOutfit;
        },
        resetCurrentOutfit(state) {
            state.currentOutfit = {};
        },
        resetState(state){
            state.currentOutfit = {};
        },
        changeOutfitProduct(state, payload) {
            const { index, product } = payload;
            
            state.currentOutfit.products.splice(
                index,
                1,
                Object.assign({}, state.currentOutfit.products[index], product)
            );

            state.detailedOutfits[state.currentOutfit.outfit_id] = state.currentOutfit;
        },
        changeCurrentProduct(state, payload) {
            const { currentProductIndex } = payload;
            
            state.currentOutfit = {
                ...state.currentOutfit,
                currentProductIndex,
            };

            state.detailedOutfits[state.currentOutfit.outfit_id] = state.currentOutfit;
        }
    },
    actions: {
        fetchOutfit(context, payload) {
            const { outfitId, outfitType, productIdsIgnoreSelect = [], ...restOfPayload } = payload;
            const { detailedOutfits } = context.state;
            let outfitFromState, outfit, productIdsIgnoreSelectFromState = [];
            
            switch(outfitType){
                case OUTFIT_TYPES.SAVED:
                    outfit = outfitFromState = context.rootGetters['savedOutfits/savedOutfitCurrent'];
                    break;
                case OUTFIT_TYPES.FEATURED:
                    outfit = outfitFromState = context.rootGetters['featuredOutfits/featuredOutfits'].find(outfit => outfit.outfit_id === outfitId);
                    break;
                case OUTFIT_TYPES.GENERATED:
                    outfit = outfitFromState = context.rootGetters['generatedOutfits/generatedOutfitCurrent'];
                    break;
                case OUTFIT_TYPES.QUIZ:
                    outfit = outfitFromState = context.rootGetters['quizzes/outfits'].find(outfit => outfit.outfit_id === outfitId);
                    break;
                case OUTFIT_TYPES.CART:
                    outfitFromState = {
                        products: context.rootGetters['cart/getCartOutfits'][outfitId],
                    };
                    if(outfitFromState) {
                        productIdsIgnoreSelectFromState = Object.values(outfitFromState.products).map(product => product.product_id);
                    }
                    break;
            }

            if(outfit) {
                if(detailedOutfits[outfitId]){
                    const detailedOutfit = {
                        ...detailedOutfits[outfitId],
                        products: detailedOutfits[outfitId].products.filter((product) =>
                            outfit.products.some(
                                (outfitProduct) =>
                                    outfitProduct.product_id === product.product_id &&
                                    outfitProduct.product_option_value_id === product.product_option_value_id
                            )
                        ),
                    };
    
                    outfit = {
                        ...outfit,
                        ...detailedOutfit,
                        isDetailed: !!detailedOutfit,
                    }
                }
            }

            if(!outfit || !outfit.isDetailed) {
                const uri = api.outfitsUri();
                // Change to /detailed/
                return api.get(`${uri}/${outfitId}`)
                    .then(response => {

                        let products = response.outfit.products;

                        // If outfit exists in state, preserve order of products and prioritize state outfit data
                        if (outfit && outfit.products) {
                            products = outfit.products.map((product) => {
                                return {
                                    ...products.find((productFromApi) => productFromApi.product_id === product.product_id),
                                    ...product,
                                };
                            })
                        }
                        
                        return context.commit('setCurrentOutfit', {
                            outfit: {
                                ...response.outfit,
                                products,
                                isDetailed: true,
                            },
                            outfitFromState,
                            outfitType,
                            productIdsIgnoreSelect: [
                                ...productIdsIgnoreSelect,
                                ...productIdsIgnoreSelectFromState,
                            ],
                            ...restOfPayload,
                        });
                    })
                    .catch(error => {
                        trackEvent("fetch_outfit_error", error);
                        return Promise.reject(error);
                    });
            } else {
                return context.commit('setCurrentOutfit', {
                    outfit,
                    outfitFromState,
                    outfitType,
                    productIdsIgnoreSelect: [
                        ...productIdsIgnoreSelect,
                        ...productIdsIgnoreSelectFromState,
                    ],
                    ...restOfPayload
                });
            }
        },
        changeOutfitProduct(context, payload) {
            const { currentOutfit: outfit } = context.state;
            const outfitNamespace = getOutfitNamespace(outfit.type);

            if(outfitNamespace) context.commit(`${outfitNamespace}/changeOutfitProduct`, { ...payload, outfitId: outfit.outfit_id }, { root: true });

            context.commit('changeOutfitProduct', { ...payload });
        },
        changeCurrentProduct(context, payload) {
            const { currentOutfit: outfit } = context.state;
            const outfitNamespace = getOutfitNamespace(outfit.type);

            if(outfitNamespace) context.commit(`${outfitNamespace}/changeCurrentProduct`, { ...payload, outfitId: outfit.outfit_id }, { root: true });

            context.commit('changeCurrentProduct', { ...payload });
        },
        updateCurrentOutfit(context, products) {
            const { currentOutfit } = context.state;
            const uri = api.outfitsUri();
            return api
                .post(uri,
                    {
                        products: products,
                        outfit_id_original: currentOutfit.outfit_id
                    }
                )
                .then(response => {
                    const { outfit } = response.body;
                    context.commit('setCurrentOutfit', {
                        outfit: {
                            ...outfit,
                            
                        },
                        outfitFromState: {
                            products,
                        },
                        outfitType: currentOutfit.type,
                    });
                    const outfitNamespace = getOutfitNamespace(currentOutfit.type);
                    context.commit(`${outfitNamespace}/setCurrentOutfit`, { ...outfit, products }, { root: true });
                    return 'Current Outfit Updated';
                })
                .catch(error => {
                    console.log(error);
                    return Promise.reject(error);
                });
        },
        swapProductFromOutfitCurrent(context, payload) {
            const { removeProduct, swapProduct } = payload;
            const { currentOutfit } = context.state;
	        const productIndex = currentOutfit.products.findIndex(product => product.product_id == removeProduct.product_id);
            const newProduct = {
                ...removeProduct,
                ...swapProduct,
                outfit_priority: swapProduct.outfit_priority || removeProduct.outfit_priority,
                outfit_priority_parent: swapProduct.outfit_priority_parent || removeProduct.outfit_priority_parent,
                outfit_id: removeProduct.outfit_id,
            };

	        if (productIndex !== -1) {
                events.trackEvent(
                    'swap_product',
                    {
                        outfitId: currentOutfit.outfit_id,
                        outfitAltered: true,
                        originalProductId: removeProduct.product_id,
                        swappedProductId: swapProduct.product_id,
                        isPersonalized: currentOutfit.outfit_featured,
                        outfitScore: currentOutfit.outfit_score,
                        gaeVersion: context.rootGetters['products/gaeVersion'],
                    }
                );
                trackEvent("swap_product", {content_id: removeProduct.product_id});

                if(currentOutfit.type === OUTFIT_TYPES.GENERATED || currentOutfit.type === OUTFIT_TYPES.SAVED) {
                    let products = [...currentOutfit.products];
                    products.splice(
                        productIndex,
                        1,
                        newProduct
                    );
                    return context.dispatch('updateCurrentOutfit', products);
                } else {
                    context.dispatch('changeOutfitProduct', {
                        index: productIndex,
                        product: newProduct,
                        outfitId: currentOutfit.outfit_id
                    }); 
                }
	        }
            
            context.dispatch('products/changeCurrentProduct', { product: swapProduct }, { root: true });
        },
    }
};
